import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaEye, FaTrash, FaTimes, FaSave, FaPrint } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import jsPDF from 'jspdf';
import { createReturn, fetchReturns, deleteReturn } from '../../services/api';
import api from '../../services/api';
import './Billing.css';
import './ReturnProduct.css';

const ReturnProduct = () => {
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [returns, setReturns] = useState([]);
  // Removed users state as Received By field is no longer used
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [form, setForm] = useState({ companyId: '', productId: '', quantity: 1, rate: '', discount: 0, reason: '', condition: 'other', customerId: '', notes: '' });
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingReturns, setViewingReturns] = useState([]);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [deletingReturn, setDeletingReturn] = useState(null);

  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProductItems, setFilteredProductItems] = useState([]);
  const [activeProductIndex, setActiveProductIndex] = useState(-1);

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomerItems, setFilteredCustomerItems] = useState([]);
  const [activeCustomerIndex, setActiveCustomerIndex] = useState(-1);

  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  // Stores the most recently created return(s) so we can print/pdf them in bulk
  const [lastCreatedReturns, setLastCreatedReturns] = useState([]);
  const productInputRef = useRef(null);

  const loadProducts = async () => {
    try {
      const params = {};
      if (form.companyId) params.company = form.companyId;
      const res = await api.get('/api/inventory', { params });
      setProducts(res.data.inventoryItems || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await api.get('/api/companies');
      setCompanies(res.data.companies || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await api.get('/api/customers');
      setCustomers(res.data.customers || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadReturns = async () => {
    try {
      const res = await fetchReturns({ search });
      const rawReturns = res.data.returns || [];

      // Sort: Today's records first, then by date descending
      const today = new Date().setHours(0, 0, 0, 0);
      const sorted = [...rawReturns].sort((a, b) => {
        const dateA = new Date(a.createdAt).setHours(0, 0, 0, 0);
        const dateB = new Date(b.createdAt).setHours(0, 0, 0, 0);

        if (dateA === today && dateB !== today) return -1;
        if (dateA !== today && dateB === today) return 1;

        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setReturns(sorted);
    } catch (err) {
      console.error(err);
    }
  };

  // Removed loading of users as Received By field is no longer used

  // Try to fetch latest billed rate/discount for a product from Billing API
  const fetchLatestPricingForProduct = async (productId) => {
    try {
      const res = await api.get('/api/billing');
      const billings = res.data?.billings || [];
      // Flatten items with createdAt context
      const matches = [];
      for (const b of billings) {
        const created = b.createdAt || b.date;
        const items = Array.isArray(b.items) ? b.items : [];
        for (const it of items) {
          const invId = it?.inventory?._id || it?.inventory;
          if (invId === productId) {
            matches.push({
              createdAt: created,
              rate: Number(it.sellingPrice || 0),
              discount: Number(it.discount || 0),
              amount: Number(it.totalAmount != null ? it.totalAmount : ((Number(it.sellingPrice || 0) - (Number(it.sellingPrice || 0) * Number(it.discount || 0) / 100)) * Number(it.quantity || 0)))
            });
          }
        }
      }
      if (matches.length === 0) return null;
      // Pick the most recent by createdAt
      matches.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return matches[0];
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    loadCompanies();
    loadCustomers();
    loadProducts();
    // Removed loadUsers();
  }, []);

  useEffect(() => {
    loadReturns();
    setCurrentPage(1);
  }, [search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = { ...form, [name]: value };

    if (name === 'companyId') {
      next.productId = '';
      next.rate = '';
      next.discount = 0;
      next.quantity = 1;
      setProductSearch('');
      setTimeout(() => {
        loadProducts();
      }, 0);
    }
    setForm(next);
  };

  const handleProductSearch = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    setForm(prev => ({ ...prev, productId: '' }));

    const query = getLastProductSearchTerm(value);
    if (query.length >= 1) {
      const filtered = products.filter(p =>
        (p.itemName || '').toLowerCase().includes(query.toLowerCase()) ||
        (p.itemCode || '').toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProductItems(filtered);
      setShowProductDropdown(true);
      setActiveProductIndex(-1);
    } else {
      setShowProductDropdown(false);
    }
  };

  const handleProductKeyDown = (e) => {
    if (!showProductDropdown || filteredProductItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveProductIndex(prev => (prev < filteredProductItems.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveProductIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeProductIndex >= 0 && activeProductIndex < filteredProductItems.length) {
        selectProduct(filteredProductItems[activeProductIndex]);
      }
    }
  };

  const selectProduct = (p) => {
    // When the user selects from the dropdown, replace the current typed term
    // (last segment) with the chosen product code.
    const termSeparator = /[\n,]+/;
    const parts = (productSearch || '').split(termSeparator).map(t => t.trim()).filter(Boolean);
    const replacement = String(p.itemCode || p.itemName || '').trim();

    if (parts.length === 0) {
      parts.push(replacement);
    } else {
      parts[parts.length - 1] = replacement;
    }

    const newProductSearch = parts.join(', ');

    const next = { ...form, productId: p._id };
    next.rate = p.sellingPrice ?? '';
    next.discount = (p.discount != null ? p.discount : 0);
    if (!next.quantity || Number(next.quantity) <= 0) next.quantity = 1;

    setForm(next);
    setProductSearch(newProductSearch);
    setShowProductDropdown(false);

    // Return focus to product input so user can continue typing immediately
    if (productInputRef.current) {
      productInputRef.current.focus();
    }

    // Try to override rate/discount using latest billing for this product
    setTimeout(async () => {
      const latest = await fetchLatestPricingForProduct(p._id);
      if (latest) {
        setForm((prev) => ({
          ...prev,
          rate: latest.rate,
          discount: latest.discount,
        }));
      }
    }, 0);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setForm(prev => ({ ...prev, customerId: '' }));

    if (value.length >= 1) {
      const filtered = customers.filter(c =>
        (c.firstName || c.name || '').toLowerCase().includes(value.toLowerCase()) ||
        (c.lastName || '').toLowerCase().includes(value.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCustomerItems(filtered);
      setShowCustomerDropdown(true);
      setActiveCustomerIndex(-1);
    } else {
      setShowCustomerDropdown(false);
    }
  };

  const handleCustomerKeyDown = (e) => {
    if (!showCustomerDropdown || filteredCustomerItems.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev < filteredCustomerItems.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveCustomerIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeCustomerIndex >= 0 && activeCustomerIndex < filteredCustomerItems.length) {
        selectCustomer(filteredCustomerItems[activeCustomerIndex]);
      }
    }
  };

  const selectCustomer = (c) => {
    setForm(prev => ({ ...prev, customerId: c._id }));
    setCustomerSearch(`${c.firstName || c.name} ${c.lastName || ''}`);
    setShowCustomerDropdown(false);
  };

  const selectedProduct = useMemo(() => products.find(p => p._id === form.productId), [products, form.productId]);

  const formatDateDDMMYY = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  };

  const getLastProductSearchTerm = (value) => {
    if (!value) return '';
    const parts = value.split(/[\s,\n]+/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  };

  const openCreateModal = () => {
    setForm({ companyId: '', productId: '', quantity: 1, rate: '', discount: 0, reason: '', condition: 'other', customerId: '', notes: '' });
    setProductSearch('');
    setCustomerSearch('');
    setError('');
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setViewingReturns([]);
    setDeletingReturn(null);
    setError('');
  };

  const openViewModal = async (ret) => {
    if (!ret) return;

    const loadBatch = async (batchId) => {
      try {
        setLoading(true);
        const res = await fetchReturns({ batchId });
        return res.data.returns || [ret];
      } catch (err) {
        console.error('Error fetching batch returns:', err);
        return [ret];
      } finally {
        setLoading(false);
      }
    };

    if (ret.batchId) {
      const batch = await loadBatch(ret.batchId);
      setViewingReturns(batch);
    } else {
      setViewingReturns([ret]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const productCodes = (productSearch || '')
        .split(/[\n,]+/)
        .map(code => code.trim())
        .filter(Boolean);

      if (productCodes.length === 0 || Number(form.quantity) <= 0) {
        throw new Error('Please enter at least one product number and a valid quantity');
      }

      const productsToCreate = productCodes.map(code => {
        const normalized = code.toLowerCase();
        const match = products.find(p =>
          ((p.itemCode || '').toLowerCase() === normalized) ||
          ((p.itemName || '').toLowerCase() === normalized)
        );
        return { code, match };
      });

      const missing = productsToCreate.filter(p => !p.match);
      if (missing.length > 0) {
        throw new Error(`Product not found: ${missing.map(p => (p.code || '').trim() || 'UNKNOWN').join(', ')}`);
      }

      const batchId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `batch-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const createdReturns = [];
      for (const { match } of productsToCreate) {
        const payload = {
          productId: match._id,
          quantity: Number(form.quantity),
          rate: form.rate === '' ? undefined : Number(form.rate),
          discount: Number(form.discount || 0),
          reason: form.reason,
          condition: form.condition,
          customerId: form.customerId || undefined,
          notes: form.notes,
          batchId
        };
        const res = await createReturn(payload);
        createdReturns.push(res.data.return);
      }

      setLastCreatedReturns(createdReturns);
      setForm({ companyId: '', productId: '', quantity: 1, rate: '', discount: 0, reason: '', condition: 'other', customerId: '', notes: '' });
      setProductSearch('');
      setCustomerSearch('');
      await loadReturns();
      await loadProducts();
      closeModal();
      setShowPrintConfirm(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to record return');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingReturn) return;
    try {
      await deleteReturn(deletingReturn._id);
      await loadReturns();
      closeModal();
    } catch (err) {
      console.error(err);
      setError('Failed to delete');
    }
  };

  const handlePrint = (returnsOrReturn) => {
    const returns = Array.isArray(returnsOrReturn) ? returnsOrReturn : (returnsOrReturn ? [returnsOrReturn] : []);
    if (returns.length === 0) return;

    const primary = returns[0];
    const customerName = (primary.customer?.firstName || primary.customer?.name || 'N/A') + (primary.customer?.lastName ? ' ' + primary.customer.lastName : '');
    const date = primary.createdAt ? new Date(primary.createdAt).toLocaleDateString() : '';

    const rows = returns.map(r => {
      const rate = Number(r.rate ?? r.product?.sellingPrice ?? 0);
      const disc = Number(r.discount || 0);
      const amount = (rate - rate * (disc / 100)) * Number(r.quantity || 0);
      return {
        id: String(r._id).slice(-8).toUpperCase(),
        name: r.itemName,
        code: r.itemCode,
        condition: r.condition,
        qty: Number(r.quantity || 0),
        rate,
        disc,
        amount
      };
    });

    const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Return Receipt</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            .meta { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .meta div { line-height: 1.4; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #f8f9fa; }
            .totals { margin-top: 20px; text-align: right; font-weight: bold; font-size: 1.2rem; }
            .footer { margin-top: 50px; text-align: center; font-size: 0.8rem; color: #777; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>RETURN RECEIPT</h1>
            <p>${returns.length > 1 ? `${returns.length} Items` : `ID: ${rows[0].id}`}</p>
          </div>
          <div class="meta">
            <div>
              <p><strong>Date:</strong> ${date}</p>
              <p><strong>Customer:</strong> ${customerName}</p>
            </div>
            <div style="text-align: right">
              <p><strong>Returned Items:</strong> ${returns.length}</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Code</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Disc</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(row => `
                <tr>
                  <td>${row.name}</td>
                  <td>${row.code}</td>
                  <td>${row.qty}</td>
                  <td>₹${row.rate.toFixed(2)}</td>
                  <td>${row.disc}%</td>
                  <td>₹${row.amount.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="totals">
            Total Refund Amount: ₹${totalAmount.toFixed(2)}
          </div>
          <div class="footer">
            <p>Thank you! | Copyright @ Centum World</p>
          </div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadPdf = (returnsOrReturn) => {
    const returns = Array.isArray(returnsOrReturn) ? returnsOrReturn : (returnsOrReturn ? [returnsOrReturn] : []);
    if (returns.length === 0) return;

    const primary = returns[0];
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const left = 10;
    const right = pageWidth - 12;

    const formatMoney = (n) => Number(n || 0).toFixed(2);

    // Title
    let y = 10;
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('RETURN RECEIPT', pageWidth / 2, y, { align: 'center' });

    // Header info
    y += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const customerName = (primary.customer?.firstName || primary.customer?.name || 'N/A') + (primary.customer?.lastName ? ' ' + primary.customer.lastName : '');
    doc.text(`Date: ${new Date(primary.createdAt).toLocaleDateString()}`, left, y);
    doc.text(`Customer: ${customerName}`, left, y + 6);
    doc.text(`Items: ${returns.length}`, right, y, { align: 'right' });

    // Table header
    y += 16;
    const tableLeft = left;
    const tableRight = right;
    const colWidths = { sl: 12, product: 70, code: 30, qty: 18, rate: 24, disc: 20, amount: 24 };
    const colX = {
      sl: tableLeft,
      product: tableLeft + colWidths.sl,
      code: tableLeft + colWidths.sl + colWidths.product,
      qty: tableLeft + colWidths.sl + colWidths.product + colWidths.code,
      rate: tableLeft + colWidths.sl + colWidths.product + colWidths.code + colWidths.qty,
      disc: tableLeft + colWidths.sl + colWidths.product + colWidths.code + colWidths.qty + colWidths.rate,
      amount: tableLeft + colWidths.sl + colWidths.product + colWidths.code + colWidths.qty + colWidths.rate + colWidths.disc
    };

    const drawTableHeader = (yStart) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Sl.', colX.sl + 1, yStart);
      doc.text('Product', colX.product + 1, yStart);
      doc.text('Code', colX.code + 1, yStart);
      doc.text('Qty', colX.qty + colWidths.qty - 1, yStart, { align: 'right' });
      doc.text('Rate', colX.rate + colWidths.rate - 1, yStart, { align: 'right' });
      doc.text('Disc%', colX.disc + colWidths.disc - 1, yStart, { align: 'right' });
      doc.text('Amount', colX.amount + colWidths.amount - 1, yStart, { align: 'right' });
      doc.setLineDash([1, 1], 0);
      doc.line(tableLeft, yStart + 2, tableRight, yStart + 2);
      doc.setLineDash();
    };

    drawTableHeader(y);

    const rows = returns.map((r, idx) => {
      const rate = Number(r.rate ?? r.product?.sellingPrice ?? 0);
      const disc = Number(r.discount || 0);
      const qty = Number(r.quantity || 0);
      const amount = (rate - rate * (disc / 100)) * qty;
      return {
        sl: idx + 1,
        name: r.itemName || '',
        code: r.itemCode || '',
        qty,
        rate,
        disc,
        amount
      };
    });

    let iy = y + 8;
    const rowHeight = 6;
    const maxRowsPerPage = Math.floor((pageHeight - 80) / rowHeight);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');

    rows.forEach((row, idx) => {
      if (idx > 0 && idx % maxRowsPerPage === 0) {
        doc.addPage();
        iy = 20;
        drawTableHeader(iy);
        iy += 8;
      }
      doc.text(String(row.sl), colX.sl + 1, iy);
      doc.text(row.name, colX.product + 1, iy, { maxWidth: colWidths.product - 2 });
      doc.text(row.code, colX.code + 1, iy, { maxWidth: colWidths.code - 2 });
      doc.text(String(row.qty), colX.qty + colWidths.qty - 1, iy, { align: 'right' });
      doc.text(formatMoney(row.rate), colX.rate + colWidths.rate - 1, iy, { align: 'right' });
      doc.text(row.disc ? row.disc.toFixed(0) : '', colX.disc + colWidths.disc - 1, iy, { align: 'right' });
      doc.text(formatMoney(row.amount), colX.amount + colWidths.amount - 1, iy, { align: 'right' });
      iy += rowHeight;
    });

    const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);

    iy += 10;
    doc.setFont(undefined, 'bold');
    doc.text('Total Refund:', colX.amount - 10, iy, { align: 'right' });
    doc.text(`₹${formatMoney(totalAmount)}`, colX.amount + colWidths.amount - 1, iy, { align: 'right' });

    doc.save(`Return-${primary.itemCode || primary._id}.pdf`);
  };

  const openLedgerForReturn = (ret) => {
    const target = ret || viewingReturns[0];
    if (!target) return;
    setShowLedgerModal(true);
  };

  const printBatch = async (ret) => {
    if (!ret) return;
    const batchId = ret.batchId;
    if (batchId) {
      try {
        setLoading(true);
        const res = await fetchReturns({ batchId });
        handlePrint(res.data.returns || [ret]);
      } catch (err) {
        console.error('Error printing batch:', err);
        handlePrint([ret]);
      } finally {
        setLoading(false);
      }
    } else {
      handlePrint([ret]);
    }
  };

  const downloadPdfBatch = async (ret) => {
    if (!ret) return;
    const batchId = ret.batchId;
    if (batchId) {
      try {
        setLoading(true);
        const res = await fetchReturns({ batchId });
        handleDownloadPdf(res.data.returns || [ret]);
      } catch (err) {
        console.error('Error generating PDF batch:', err);
        handleDownloadPdf([ret]);
      } finally {
        setLoading(false);
      }
    } else {
      handleDownloadPdf([ret]);
    }
  };

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div>
          <h1 className="billing-title">Return Product</h1>
          <p className="billing-subtitle">Record and manage returned products</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="add-billing-btn" onClick={openCreateModal}>
            <FaPlus /> Add Return
          </button>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search returns by item, code, reason..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <div className="filter-row">
          <button onClick={loadReturns} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="billing-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {returns.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage + 1)}-
            {Math.min(returns.length, currentPage * itemsPerPage)} of {returns.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="pageSize" style={{ color: '#6c757d', fontSize: '0.9rem' }}>Rows per page:</label>
            <select
              id="pageSize"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ minWidth: 100 }}
            >
              {[10, 20, 50, 100].map(sz => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              Page {currentPage} of {Math.max(1, Math.ceil(returns.length / itemsPerPage))}
            </span>
            <button
              className="btn-secondary"
              onClick={() => setCurrentPage(p => Math.min(Math.max(1, Math.ceil(returns.length / itemsPerPage)), p + 1))}
              disabled={currentPage >= Math.max(1, Math.ceil(returns.length / itemsPerPage))}
            >
              Next
            </button>
          </div>
        </div>
        <table className="billing-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Item</th>
              <th>Code</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Discount</th>
              <th>Amount</th>
              <th>Condition</th>
              <th>Reason</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {returns.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center' }}>No returns found.</td>
              </tr>
            ) : (
              returns.slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage).map(r => (
                <tr key={r._id}>
                  <td>{formatDateDDMMYY(r.createdAt)}</td>
                  <td>{r.itemName}</td>
                  <td>{r.itemCode}</td>
                  <td>{r.quantity}</td>
                  <td>₹{Number(r.rate ?? r.product?.sellingPrice ?? 0).toFixed(2)}</td>
                  <td>{Number(r.discount || 0)}%</td>
                  <td>₹{Number(r.amount ?? ((Number(r.rate ?? r.product?.sellingPrice ?? 0) - (Number(r.rate ?? r.product?.sellingPrice ?? 0) * Number(r.discount || 0) / 100)) * Number(r.quantity || 0))).toFixed(2)}</td>
                  <td>{r.condition}</td>
                  <td>{r.reason || '-'}</td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => openViewModal(r)}
                        className="action-btn view"
                        title="View Return"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => printBatch(r)}
                        className="action-btn print"
                        title="Print Return"
                      >
                        <FaPrint /> Print
                      </button>
                      <button
                        onClick={() => downloadPdfBatch(r)}
                        className="action-btn edit"
                        title="Download PDF (Half A4)"
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => setDeletingReturn(r)}
                        className="action-btn delete"
                        title="Delete Return"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Returned Product</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="billing-form">
              <div className="form-section">
                <h3>Return Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Company</label>
                    <select name="companyId" value={form.companyId} onChange={handleChange}>
                      <option value="">All companies</option>
                      {companies.map(c => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Product *</label>
                    <input
                      type="text"
                      value={productSearch}
                      onChange={handleProductSearch}
                      onKeyDown={handleProductKeyDown}
                      ref={productInputRef}
                      placeholder="Type product codes (comma separated) or search..."
                      autoComplete="off"
                      required
                    />
                    {showProductDropdown && (
                      <div className="dropdown-list">
                        {filteredProductItems.length > 0 ? (
                          filteredProductItems.map((p, index) => (
                            <div
                              key={p._id}
                              onClick={() => selectProduct(p)}
                              className={`dropdown-item ${activeProductIndex === index ? 'active' : ''}`}
                            >
                              {p.itemName} ({p.itemCode})
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-item">No products found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input type="number" name="quantity" min="1" value={form.quantity} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Condition</label>
                    <select name="condition" value={form.condition} onChange={handleChange}>
                      <option value="new">New</option>
                      <option value="opened">Opened</option>
                      <option value="damaged">Damaged</option>
                      <option value="defective">Defective</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Rate</label>
                    <input type="number" name="rate" value={form.rate} onChange={handleChange} placeholder="Leave blank to use product price" min="0" step="0.01" />
                  </div>
                  <div className="form-group">
                    <label>Discount (%)</label>
                    <input type="number" name="discount" value={form.discount} onChange={handleChange} min="0" max="100" />
                  </div>
                  <div className="form-group">
                    <label>Total Amount</label>
                    <input type="text" value={`₹${((Number((form.rate === '' ? (selectedProduct?.sellingPrice ?? 0) : form.rate) || 0) - (Number((form.rate === '' ? (selectedProduct?.sellingPrice ?? 0) : form.rate) || 0) * Number(form.discount || 0) / 100)) * Number(form.quantity || 0)).toFixed(2)}`}
                      readOnly />
                  </div>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Customer</label>
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={handleCustomerSearchChange}
                      onKeyDown={handleCustomerKeyDown}
                      placeholder="Type to search customer..."
                      autoComplete="off"
                    />
                    {showCustomerDropdown && (
                      <div className="dropdown-list">
                        {filteredCustomerItems.length > 0 ? (
                          filteredCustomerItems.map((c, index) => (
                            <div
                              key={c._id}
                              onClick={() => selectCustomer(c)}
                              className={`dropdown-item ${activeCustomerIndex === index ? 'active' : ''}`}
                            >
                              {c.firstName || c.name} {c.lastName || ''} {c.email ? `(${c.email})` : ''}
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-item">No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Received By field removed as per requirement */}
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Reason</label>
                    <input type="text" name="reason" value={form.reason} onChange={handleChange} placeholder="Optional reason" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Notes</label>
                    <textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Optional notes" rows={2} />
                  </div>
                </div>

                {selectedProduct && (
                  <div className="items-list">
                    <h4>Selected Product Snapshot</h4>
                    <div className="items-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Code</th>
                            <th>Stock</th>
                            <th>Rate</th>
                            <th>Discount</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{selectedProduct.itemName}</td>
                            <td>{selectedProduct.itemCode}</td>
                            <td>{selectedProduct.itemQuantity}</td>
                            <td>₹{Number((form.rate === '' ? selectedProduct.sellingPrice : form.rate) || 0).toFixed(2)}</td>
                            <td>{Number(form.discount || 0)}%</td>
                            <td>₹{((Number((form.rate === '' ? selectedProduct.sellingPrice : form.rate) || 0) - (Number((form.rate === '' ? selectedProduct.sellingPrice : form.rate) || 0) * Number(form.discount || 0) / 100)) * Number(form.quantity || 0)).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={loading}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FaSave /> {loading ? 'Saving...' : 'Add Return'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingReturns.length > 0 && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Return Details</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="billing-view-content">
              <div className="billing-info-section">
                <div className="detail-group">
                  <h3>Batch</h3>
                  <div className="detail-item"><label>Batch ID:</label><span>{viewingReturns[0].batchId || '-'}</span></div>
                  <div className="detail-item"><label>Date:</label><span>{formatDateDDMMYY(viewingReturns[0].createdAt)}</span></div>
                  <div className="detail-item"><label>Customer:</label><span>{(viewingReturns[0].customer?.firstName || viewingReturns[0].customer?.name || '') + (viewingReturns[0].customer?.lastName ? ' ' + viewingReturns[0].customer.lastName : '') || '-'}</span></div>
                  <div className="detail-item"><label>Items:</label><span>{viewingReturns.length}</span></div>
                </div>
                <div className="detail-group">
                  <h3>Items</h3>
                  <div className="items-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Code</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Discount</th>
                          <th>Amount</th>
                          <th>Condition</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingReturns.map((r) => {
                          const rate = Number(r.rate ?? r.product?.sellingPrice ?? 0);
                          const disc = Number(r.discount || 0);
                          const amount = r.amount != null ? Number(r.amount) : (rate - rate * (disc / 100)) * Number(r.quantity || 0);
                          return (
                            <tr key={r._id}>
                              <td>{r.itemName}</td>
                              <td>{r.itemCode}</td>
                              <td>{r.quantity}</td>
                              <td>₹{rate.toFixed(2)}</td>
                              <td>{disc}%</td>
                              <td>₹{amount.toFixed(2)}</td>
                              <td>{r.condition}</td>
                              <td>{r.reason || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="notes-section">
                <h3>Notes</h3>
                <p>{viewingReturns[0].notes || '-'}</p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' }}>
              <button className="btn-primary" onClick={() => openLedgerForReturn(viewingReturns[0])}>
                Add Ledger Entry
              </button>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingReturns.length > 0 ? `Return ${viewingReturns[0].itemCode} - ${viewingReturns[0].itemName}` : ''}
      />

      {/* Delete Modal */}
      {deletingReturn && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Return</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              Are you sure you want to delete this return for <strong>{deletingReturn.itemName}</strong> ({deletingReturn.itemCode})?
            </div>
            <div className="form-actions" style={{ paddingTop: 0 }}>
              <button type="button" className="btn-secondary" onClick={closeModal}>
                <FaTimes /> Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmDelete}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrintConfirm && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <div className="modal-header">
              <h2>Print Confirmation</h2>
            </div>
            <div className="modal-body">
              <p>Return recorded successfully! Would you like to print the return receipt now?</p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="action-btn print"
                onClick={() => {
                  handlePrint(lastCreatedReturns);
                  setShowPrintConfirm(false);
                }}
              >
                <FaPrint /> Print
              </button>
              <button
                className="action-btn edit"
                onClick={() => {
                  handleDownloadPdf(lastCreatedReturns);
                  setShowPrintConfirm(false);
                }}
              >
                PDF
              </button>
              <button
                className="action-btn delete"
                onClick={() => setShowPrintConfirm(false)}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnProduct;


